// ============================================================
// ODPT API クライアント
// ============================================================
// dev: Vite が /odpt-public/* と /odpt/* をプロキシする
// prd: CloudFront distribution が同じパスで proxy + キー注入

import { ODPT_ORIGIN, ODPT_PREFIX, ODPT_PUBLIC_PREFIX, OPERATORS } from '../config'

export class OdptError extends Error {
  constructor(public status: number, public path: string, msg: string) {
    super(`ODPT ${status} on ${path}: ${msg}`)
    this.name = 'OdptError'
  }
}

async function get<T>(path: string, kind: 'public' | 'auth' = 'public'): Promise<T> {
  const prefix = kind === 'public' ? ODPT_PUBLIC_PREFIX : ODPT_PREFIX
  const url = `${ODPT_ORIGIN}${prefix}${path}`
  const res = await fetch(url, { method: 'GET', headers: { accept: 'application/json' } })
  if (!res.ok) {
    throw new OdptError(res.status, url, await res.text().catch(() => ''))
  }
  return (await res.json()) as T
}

async function getProtobuf(path: string): Promise<Uint8Array> {
  const url = `${ODPT_ORIGIN}${ODPT_PUBLIC_PREFIX}${path}`
  const res = await fetch(url, { method: 'GET' })
  if (!res.ok) {
    throw new OdptError(res.status, url, await res.text().catch(() => ''))
  }
  return new Uint8Array(await res.arrayBuffer())
}

// ───────── 公開エンドポイント ─────────

export function fetchToeiBusVehiclePositions(): Promise<Uint8Array> {
  return getProtobuf('/api/v4/gtfs/realtime/ToeiBus')
}

export function fetchToeiBusTripUpdates(): Promise<Uint8Array> {
  return getProtobuf('/api/v4/gtfs/realtime/ToeiBus_tripupdate')
}

/** 関東バス GTFS-RT VehiclePosition (auth 必要、edge proxy 経由) */
async function getProtobufAuth(path: string): Promise<Uint8Array> {
  const url = `${ODPT_ORIGIN}${ODPT_PREFIX}${path}`
  const res = await fetch(url, { method: 'GET' })
  if (!res.ok) {
    throw new OdptError(res.status, url, await res.text().catch(() => ''))
  }
  return new Uint8Array(await res.arrayBuffer())
}

export function fetchKantoBusVehiclePositions(): Promise<Uint8Array> {
  return getProtobufAuth('/api/v4/gtfs/realtime/odpt_KantoBus_AllLines_vehicle')
}

export function fetchKantoBusTripUpdates(): Promise<Uint8Array> {
  return getProtobufAuth('/api/v4/gtfs/realtime/odpt_KantoBus_AllLines_trip_update')
}

export function fetchKantoBusAlerts(): Promise<Uint8Array> {
  return getProtobufAuth('/api/v4/gtfs/realtime/odpt_KantoBus_AllLines_alert')
}

/** 都営地下鉄+さくらトラムのリアルタイム列車位置 (GTFS-RT) */
export function fetchToeiTrainVehiclePositions(): Promise<Uint8Array> {
  return getProtobuf('/api/v4/gtfs/realtime/toei_odpt_train_vehicle')
}

/** 都営地下鉄+さくらトラムのリアルタイム TripUpdate */
export function fetchToeiTrainTripUpdates(): Promise<Uint8Array> {
  return getProtobuf('/api/v4/gtfs/realtime/toei_odpt_train_trip_update')
}

/** 都営の運行情報 alert (GTFS-RT Alert) */
export function fetchToeiTrainAlerts(): Promise<Uint8Array> {
  return getProtobuf('/api/v4/gtfs/realtime/toei_odpt_train_alert')
}

/** 東京メトロの GTFS-RT Alert */
export function fetchTokyoMetroTrainAlerts(): Promise<Uint8Array> {
  return getProtobufAuth('/api/v4/gtfs/realtime/tokyometro_odpt_train_alert')
}

/** TX (MIR) の GTFS-RT Alert */
export function fetchMirTrainAlerts(): Promise<Uint8Array> {
  return getProtobufAuth('/api/v4/gtfs/realtime/mir_odpt_train_alert')
}

/** TWR (りんかい線) の GTFS-RT Alert */
export function fetchTwrTrainAlerts(): Promise<Uint8Array> {
  return getProtobufAuth('/api/v4/gtfs/realtime/twr_odpt_train_alert')
}

// ───────── 認証エンドポイント ─────────

export interface OdptStation {
  '@type': 'odpt:Station'
  'owl:sameAs': string
  'dc:title'?: string
  'odpt:operator': string
  'odpt:railway': string
  'geo:lat': number
  'geo:long': number
}

export interface OdptBusstopPole {
  '@type': 'odpt:BusstopPole'
  'owl:sameAs': string
  'dc:title'?: string
  'odpt:operator': string
  'geo:lat': number
  'geo:long': number
}

export interface OdptTrain {
  '@type': 'odpt:Train'
  'owl:sameAs': string
  'dc:date'?: string
  'odpt:operator': string
  'odpt:railway': string
  'odpt:trainNumber'?: string
  'odpt:fromStation'?: string
  'odpt:toStation'?: string
  'odpt:railDirection'?: string
  'odpt:delay'?: number
}

export function fetchStations(operatorIds: string[] = [OPERATORS.toei]): Promise<OdptStation[]> {
  const q = encodeURIComponent(operatorIds.join(','))
  return get<OdptStation[]>(`/api/v4/odpt:Station?odpt:operator=${q}`, 'auth')
}

export function fetchBusstops(operatorIds: string[] = [OPERATORS.toei]): Promise<OdptBusstopPole[]> {
  const q = encodeURIComponent(operatorIds.join(','))
  return get<OdptBusstopPole[]>(`/api/v4/odpt:BusstopPole?odpt:operator=${q}`, 'auth')
}

export function fetchTrains(operatorIds: string[] = [OPERATORS.toei, OPERATORS.tokyoMetro]): Promise<OdptTrain[]> {
  const q = encodeURIComponent(operatorIds.join(','))
  return get<OdptTrain[]>(`/api/v4/odpt:Train?odpt:operator=${q}`, 'auth')
}

// ───── 詳細ページで使う追加データ ─────

export interface OdptStationTimetable {
  '@type': 'odpt:StationTimetable'
  'owl:sameAs': string
  'odpt:station': string
  'odpt:railway': string
  'odpt:railDirection'?: string
  'odpt:stationTimetableObject'?: Array<{
    'odpt:departureTime'?: string  // "12:34"
    'odpt:destinationStation'?: string[]
    'odpt:trainType'?: string
  }>
}

export interface OdptTrainInformation {
  '@type': 'odpt:TrainInformation'
  'owl:sameAs': string
  'odpt:operator': string
  'odpt:railway'?: string
  'odpt:trainInformationText'?: { ja?: string; en?: string }
  'odpt:trainInformationStatus'?: { ja?: string; en?: string }
  'dc:date'?: string
}

export interface OdptPassengerSurvey {
  '@type': 'odpt:PassengerSurvey'
  'owl:sameAs': string
  'odpt:station': string
  'odpt:passengerSurveyObject'?: Array<{
    'odpt:surveyYear': number
    'odpt:passengerJourneys': number
  }>
}

export function fetchStationTimetables(stationId: string): Promise<OdptStationTimetable[]> {
  const q = encodeURIComponent(stationId)
  return get<OdptStationTimetable[]>(`/api/v4/odpt:StationTimetable?odpt:station=${q}`, 'auth')
}

export function fetchTrainInformation(operatorIds: string[] = [OPERATORS.toei]): Promise<OdptTrainInformation[]> {
  const q = encodeURIComponent(operatorIds.join(','))
  return get<OdptTrainInformation[]>(`/api/v4/odpt:TrainInformation?odpt:operator=${q}`, 'auth')
}

export function fetchPassengerSurvey(stationId: string): Promise<OdptPassengerSurvey[]> {
  const q = encodeURIComponent(stationId)
  return get<OdptPassengerSurvey[]>(`/api/v4/odpt:PassengerSurvey?odpt:station=${q}`, 'auth')
}
